// What people ask Ask HR and how it does, for HR.Agent and HR.Admin. Questions appear without
// names: identity only ever shows on Talk to HR requests.

import { useEffect, useState } from "react";
import { CATEGORIES, type Category } from "@ask-hr/core/escalations";
import { api } from "../api.js";
import { Seg } from "../components/Seg.js";
import { niceMax } from "./chart.js";
import { TopPlaces, UsageMap } from "./UsageMap.js";

interface Summary {
  days: number;
  totals: {
    questions: number;
    answered: number;
    notCovered: number;
    helpful: number;
    notHelpful: number;
    web: number;
    slack: number;
    medianSeconds: number | null;
    p90Seconds: number | null;
  };
  byDay: { day: string; web: number; slack: number }[];
  audiences: Record<string, number>;
  places: Record<string, number>; // "US-TX" | "CANADA" | "US" (state not recorded) | "unknown"
  topDocuments: { title: string; url: string; count: number }[];
  notCovered: { at: string; channel: string; question: string; answer: string }[];
  notHelpful: { at: string; channel: string; question: string; answer: string; sources: { title: string; label: string; url: string }[] }[];
  escalations: { total: number; open: number; byCategory: Record<Category, number>; medianHoursToResolve: number | null };
  cost: { usd: number; perQuestion: number | null; cacheSavingsUsd: number };
}

const AUDIENCE_NAMES: Record<string, string> = {
  "us-ca": "California",
  "us-other": "Another US state",
  canada: "Canada",
  unknown: "Not set",
};

const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
const pctOf = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : "–");
const money = (n: number) => n.toLocaleString([], { style: "currency", currency: "USD", minimumFractionDigits: 2 });

export function AnalyticsPage() {
  const [range, setRange] = useState<"7" | "30" | "90">("30");
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    const tz = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
    api<Summary>("GET", `/api/admin/analytics?days=${range}&tz=${tz}`).then(
      (d) => live && (setData(d), setError(false)),
      () => live && setError(true),
    );
    return () => {
      live = false;
    };
  }, [range]);

  // A link from a miss notice (#not-covered): go to that list once it's on the page.
  const loaded = data !== null;
  useEffect(() => {
    if (loaded && window.location.hash === "#not-covered") document.getElementById("not-covered")?.scrollIntoView();
  }, [loaded]);

  const t = data?.totals;
  const rated = t ? t.helpful + t.notHelpful : 0;

  return (
    <>
      <section className="panel">
        <div className="toolbar">
          <h2 className="inline">Analytics</h2>
          <Seg
            label="Period"
            value={range}
            onChange={setRange}
            options={[
              ["7", "7 days"],
              ["30", "30 days"],
              ["90", "90 days"],
            ]}
          />
        </div>
        <p className="sub">
          Questions are shown without names; who asked is never recorded here. Records are kept for 90 days.
        </p>
        {error ? (
          <p className="notice" role="alert">
            Couldn't load analytics. Try again.
          </p>
        ) : null}
        {!data && !error ? <p className="sub">Loading…</p> : null}
        {t ? (
          <div className="kpis">
            <Kpi label="Questions" value={t.questions} detail={`${t.web} web · ${t.slack} Slack`} />
            <Kpi label="Answered with sources" value={pctOf(t.answered, t.questions)} detail={`${t.notCovered} not covered by the documents`} />
            <Kpi label="Helpful" value={rated ? pctOf(t.helpful, rated) : "–"} detail={`${t.helpful} 👍 · ${t.notHelpful} 👎`} />
            <Kpi
              label="Response time"
              value={t.medianSeconds === null ? "–" : `${t.medianSeconds}s`}
              detail={t.p90Seconds === null ? "median" : `median · 90% within ${t.p90Seconds}s`}
            />
            <Kpi
              label="Talk to HR"
              value={data?.escalations.total ?? 0}
              detail={`${data?.escalations.open ?? 0} open${data?.escalations.medianHoursToResolve != null ? ` · resolved in ${data.escalations.medianHoursToResolve}h (median)` : ""}`}
            />
            <Kpi
              label="Estimated cost"
              value={money(data?.cost.usd ?? 0)}
              detail={`${data?.cost.perQuestion != null ? `${money(data.cost.perQuestion)} a question · ` : ""}cache saved ${money(data?.cost.cacheSavingsUsd ?? 0)}`}
            />
          </div>
        ) : null}
      </section>

      {data ? (
        <>
          <section className="panel" aria-labelledby="volume-h">
            <h2 id="volume-h">Questions per day</h2>
            <DayChart days={data.byDay} />
            <p className="legend">
              <span className="swatch web" /> Web <span className="swatch slack" /> Slack
              {Object.keys(data.audiences).length ? (
                <span className="muted">
                  {" "}
                  · By location:{" "}
                  {Object.entries(data.audiences)
                    .sort((a, b) => b[1] - a[1])
                    .map(([a, n]) => `${AUDIENCE_NAMES[a] ?? a} ${n}`)
                    .join(", ")}
                </span>
              ) : null}
            </p>
          </section>

          <section className="panel" aria-labelledby="map-h">
            <h2 id="map-h">Where questions come from</h2>
            <div className="map-wrap">
              <div>
                <UsageMap places={data.places} />
              </div>
              <TopPlaces places={data.places} />
            </div>
          </section>

          <div className="grid2">
            <section className="panel" aria-labelledby="docs-h">
              <h2 id="docs-h">Most-cited documents</h2>
              {data.topDocuments.length ? (
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Document</th>
                      <th scope="col" className="right">
                        Answers
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topDocuments.map((d) => (
                      <tr key={d.title}>
                        <td>
                          <a href={d.url} target="_blank" rel="noreferrer">
                            {d.title}
                          </a>
                        </td>
                        <td className="right">{d.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="empty">No answers yet in this period.</p>
              )}
            </section>
            <section className="panel" aria-labelledby="hr-h">
              <h2 id="hr-h">Talk to HR by topic</h2>
              <table>
                <tbody>
                  {(Object.entries(CATEGORIES) as [Category, string][]).map(([c, label]) => (
                    <tr key={c}>
                      <td>{label}</td>
                      <td className="right">{data.escalations.byCategory[c] ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>

          <QuestionList
            id="not-covered"
            title="Not covered by the documents"
            intro="Questions Ask HR couldn't answer with a source. These show what's missing from the HR documents, or what to route better."
            items={data.notCovered}
          />
          <QuestionList id="nh" title="Marked not helpful" intro="Answers someone gave a 👎. Worth reading for wrong or unclear answers." items={data.notHelpful} />
        </>
      ) : null}
    </>
  );
}

function Kpi({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-detail">{detail}</div>
    </div>
  );
}

// Bars and labels are HTML, so text never stretches with the width; gridlines mark quarters of
// the scale.
function DayChart({ days }: { days: Summary["byDay"] }) {
  const peak = Math.max(0, ...days.map((d) => d.web + d.slack));
  const top = niceMax(peak);
  const ticks = [1, 0.75, 0.5, 0.25, 0].map((f) => Math.round(top * f * 10) / 10);
  const fmt = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString([], { month: "short", day: "numeric" });
  const every = days.length <= 10 ? 1 : days.length <= 31 ? 7 : 14;
  return (
    <figure className="chart" aria-label={`Questions per day, peak ${peak}`}>
      <div className="chart-body">
        <div className="chart-y" aria-hidden="true">
          {ticks.map((t) => (
            <span key={t}>{t}</span>
          ))}
        </div>
        <div className="chart-plot">
          {ticks.map((t) => (
            <div key={t} className="gridline" style={{ bottom: `${(t / top) * 100}%` }} aria-hidden="true" />
          ))}
          <div className="chart-cols">
            {days.map((d) => (
              <div key={d.day} className="chart-col" title={`${fmt(d.day)}: ${d.web} web, ${d.slack} Slack`}>
                <div className="bar slack" style={{ height: `${(d.slack / top) * 100}%` }} />
                <div className="bar web" style={{ height: `${(d.web / top) * 100}%` }} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="chart-x" aria-hidden="true">
        {days.map((d, i) => (
          <span key={d.day}>{i % every === 0 ? fmt(d.day) : ""}</span>
        ))}
      </div>
      <details>
        <summary>Show as a table</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col" className="right">
                Web
              </th>
              <th scope="col" className="right">
                Slack
              </th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => (
              <tr key={d.day}>
                <td>{fmt(d.day)}</td>
                <td className="right">{d.web}</td>
                <td className="right">{d.slack}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

function QuestionList({
  id,
  title,
  intro,
  items,
}: {
  id: string;
  title: string;
  intro: string;
  items: { at: string; channel: string; question: string; answer: string; sources?: { title: string; label: string; url: string }[] }[];
}) {
  return (
    <section className="panel" id={id} aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`}>
        {title} <span className="muted">({items.length}{items.length === 50 ? "+" : ""})</span>
      </h2>
      <p className="sub">{intro}</p>
      {items.length ? (
        <ul className="qlist">
          {items.map((q, i) => (
            <li key={i}>
              <details>
                <summary>
                  <span className="prewrap">{q.question}</span>
                  <span className="muted small">
                    {" "}
                    · {when(q.at)} · {q.channel === "slack" ? "Slack" : "Web"}
                  </span>
                </summary>
                <div className="qanswer">
                  <p className="prewrap">{q.answer}</p>
                  {q.sources?.length ? (
                    <p className="muted small">
                      Sources:{" "}
                      {q.sources.map((s, j) => (
                        <span key={j}>
                          {j ? ", " : ""}
                          <a href={s.url} target="_blank" rel="noreferrer">
                            {s.title}
                          </a>{" "}
                          ({s.label})
                        </span>
                      ))}
                    </p>
                  ) : null}
                </div>
              </details>
            </li>
          ))}
        </ul>
      ) : (
        <p className="empty">None in this period.</p>
      )}
    </section>
  );
}
